/**
 * 与《管理后台-兑换积分相关角色与权限说明》一致：
 * - ①～⑤ 与 POST `AdminCapabilityParam`、GET `IntegrationModuleScopeVO` 字段名一致
 * - 细粒度 `ums_role.name`：会员注册管理员、赠送积分管理员、赠送卡券管理员、卡券核销管理员、积分激活管理员
 * - 兼容旧角色：超级/系统/积分/核销/会员 管理员
 * GET 可能另含只读 `giftReasonCategoriesAllowed`（③ GET），子能力表单不展示该项
 */
export const SUPER_ADMIN_ROLE_NAME = '超级管理员'
export const SYSTEM_ADMIN_ROLE_NAME = '系统管理员'
export const INTEGRATION_MANAGER_ROLE_NAME = '积分管理员'
export const MEMBER_ADMIN_ROLE_NAME = '会员管理员'
export const VERIFICATION_ADMIN_ROLE_NAME = '核销管理员'

/** 细粒度能力角色（与文档「五个细粒度能力角色」表 `ums_role.name` 一致） */
export const FINE_MEMBER_REGISTER_ADMIN = '会员注册管理员'
export const FINE_GIFT_INTEGRATION_ADMIN = '赠送积分管理员'
export const FINE_GIFT_VERIFY_CARD_ADMIN = '赠送卡券管理员'
/** ⑦ 管理赠送核销卡券激活（与 `POST /member-verify-card/activate-admin-gift` 拦截一致） */
export const FINE_GIFT_VERIFY_CARD_ACTIVATE_ADMIN = '赠送卡券激活管理员'
export const FINE_VERIFY_CARD_ADMIN = '卡券核销管理员'
export const FINE_INTEGRATION_ACTIVATION_ADMIN = '积分激活管理员'
/** ⑥ 积分统计报表（`integrationStatsAllowed`） */
export const FINE_INTEGRATION_STATS_ADMIN = '积分统计管理员'
/** ⑧ 商品供应商管理（`supplierManagementAllowed`） */
export const FINE_SUPPLIER_ADMIN = '供应商管理员'

/** 在分配页中：凡勾选下列任一，即与「子能力/能力范围」相关，非超管时仅展示提示 */
export const ALL_CAPABILITY_RELATED_ROLE_NAMES = [
  FINE_MEMBER_REGISTER_ADMIN,
  FINE_GIFT_INTEGRATION_ADMIN,
  FINE_GIFT_VERIFY_CARD_ADMIN,
  FINE_GIFT_VERIFY_CARD_ACTIVATE_ADMIN,
  FINE_VERIFY_CARD_ADMIN,
  FINE_INTEGRATION_ACTIVATION_ADMIN,
  FINE_INTEGRATION_STATS_ADMIN,
  FINE_SUPPLIER_ADMIN,
  MEMBER_ADMIN_ROLE_NAME,
  INTEGRATION_MANAGER_ROLE_NAME,
  VERIFICATION_ADMIN_ROLE_NAME,
  SYSTEM_ADMIN_ROLE_NAME,
  SUPER_ADMIN_ROLE_NAME
]

/** 各子能力在界面展示与落库时：当前账号需至少具备其中一类角色，含超级则视为全量可配 */
const COVER = {
  memberRegisterAllowed: [
    FINE_MEMBER_REGISTER_ADMIN,
    MEMBER_ADMIN_ROLE_NAME,
    SUPER_ADMIN_ROLE_NAME
  ],
  giftIntegrationAllowed: [
    FINE_GIFT_INTEGRATION_ADMIN,
    SYSTEM_ADMIN_ROLE_NAME,
    INTEGRATION_MANAGER_ROLE_NAME,
    SUPER_ADMIN_ROLE_NAME
  ],
  giftVerifyCardAllowed: [
    FINE_GIFT_VERIFY_CARD_ADMIN,
    SYSTEM_ADMIN_ROLE_NAME,
    INTEGRATION_MANAGER_ROLE_NAME,
    SUPER_ADMIN_ROLE_NAME
  ],
  verifyCardAllowed: [
    FINE_VERIFY_CARD_ADMIN,
    VERIFICATION_ADMIN_ROLE_NAME,
    INTEGRATION_MANAGER_ROLE_NAME,
    SUPER_ADMIN_ROLE_NAME
  ],
  integrationActivationAllowed: [
    FINE_INTEGRATION_ACTIVATION_ADMIN,
    SYSTEM_ADMIN_ROLE_NAME,
    INTEGRATION_MANAGER_ROLE_NAME,
    SUPER_ADMIN_ROLE_NAME
  ]
}

/** 与文档、后端 POST 一致的五项布尔（不含只读 giftReasonCategoriesAllowed） */
export const INTEGRATION_SCOPE_FORM_DEFAULT = () => ({
  memberRegisterAllowed: true,
  giftIntegrationAllowed: true,
  giftVerifyCardAllowed: true,
  verifyCardAllowed: true,
  integrationActivationAllowed: true
})

/**
 * 侧栏用：三模块下挂 5 个选项；每项 `coverRoleNames` 与文档「细粒度 + 兼容」对应
 */
export const INTEGRATION_SCOPE_UI_SECTIONS = [
  {
    title: '积分管理',
    options: [
      {
        key: 'giftIntegrationAllowed',
        coverRoleNames: COVER.giftIntegrationAllowed,
        label:
          '② 赠送积分（调整会员外购/兑换积分余额，不含赠送卡券合成订单；兼容：系统/积分管理员）'
      },
      {
        key: 'giftVerifyCardAllowed',
        coverRoleNames: COVER.giftVerifyCardAllowed,
        label:
          '③ 赠送卡券（管理端向会员发放核销卡券，sourceType=99；兼容：系统/积分管理员；POST 与积分拦截器一致）'
      },
      {
        key: 'integrationActivationAllowed',
        coverRoleNames: COVER.integrationActivationAllowed,
        label: '⑤ 积分激活（批次待激活→已激活、冻结/解冻/变更 VIN 等；兼容：系统/积分管理员）'
      }
    ]
  },
  {
    title: '会员管理',
    options: [
      {
        key: 'memberRegisterAllowed',
        coverRoleNames: COVER.memberRegisterAllowed,
        label: '① 会员注册（单个/批量注册与初始积分字段；细粒度：会员注册管理员；兼容：会员管理员）'
      }
    ]
  },
  {
    title: '核销管理',
    options: [
      {
        key: 'verifyCardAllowed',
        coverRoleNames: COVER.verifyCardAllowed,
        label:
          '④ 卡券核销（核销码、卡券列表等；细粒度：卡券核销管理员；兼容：核销/积分/超级，与核销拦截器一致）'
      }
    ]
  }
]

function selectedNamesCoverKey(selectedNames, key) {
  const names = new Set((selectedNames || []).filter(Boolean))
  const need = COVER[key]
  return need.some((n) => names.has(n))
}

/**
 * 按当前勾选的角色名，得到各子能力是否应勾选（与 COVER 一致）。
 * 用于分配页：勾选「会员注册管理员」等岗位时同步勾选 ①～⑤ 对应项。
 */
export function buildIntegrationScopeFlagsFromRoleNames(selectedRoleNames) {
  const out = {}
  Object.keys(COVER).forEach((k) => {
    out[k] = selectedNamesCoverKey(selectedRoleNames, k)
  })
  return out
}

/**
 * 未勾选可覆盖该能力的一类角色时，将对应 API 字段置为 false，与《说明》中 POST 语义一致
 */
export function coerceIntegrationScopeToSelectedRoles(form, roleNamesInSelection) {
  const f = { ...form }
  const keys = Object.keys(COVER)
  keys.forEach((k) => {
    if (!selectedNamesCoverKey(roleNamesInSelection, k)) {
      f[k] = false
    }
  })
  return f
}

/** 子能力在 GET body / data 里可能出现的新、旧字段名；不含只读 giftReasonCategoriesAllowed */
const SCOPE_FLAG_KEYS = [
  'memberRegisterAllowed',
  'giftIntegrationAllowed',
  'giftVerifyCardAllowed',
  'verifyCardAllowed',
  'integrationActivationAllowed',
  'memberRegister',
  'giftPoints',
  'giftCard',
  'cardVerify',
  'pointsActivation'
]

function hasAnyScopeFlagInPayload(d) {
  if (d == null || typeof d !== 'object') return false
  return SCOPE_FLAG_KEYS.some((k) => d[k] !== undefined)
}

function pickBoolStrict(d, newKey, legacyKey) {
  if (d[newKey] !== undefined) return d[newKey] === true
  if (legacyKey && d[legacyKey] !== undefined) return d[legacyKey] === true
  return false
}

/**
 * 将 GET 返回的 data 转为一表单对象；见文件头说明（含「至少一项键」时的严格解析）
 */
export function normalizeIntegrationScope(d) {
  if (d == null || typeof d !== 'object') {
    return INTEGRATION_SCOPE_FORM_DEFAULT()
  }
  if (!hasAnyScopeFlagInPayload(d)) {
    return INTEGRATION_SCOPE_FORM_DEFAULT()
  }
  return {
    memberRegisterAllowed: pickBoolStrict(d, 'memberRegisterAllowed', 'memberRegister'),
    giftIntegrationAllowed: pickBoolStrict(d, 'giftIntegrationAllowed', 'giftPoints'),
    giftVerifyCardAllowed: pickBoolStrict(d, 'giftVerifyCardAllowed', 'giftCard'),
    verifyCardAllowed: pickBoolStrict(d, 'verifyCardAllowed', 'cardVerify'),
    integrationActivationAllowed: pickBoolStrict(
      d,
      'integrationActivationAllowed',
      'pointsActivation'
    )
  }
}

/** 角色旁提示：细粒度 1 对 1，旧角色为兼容并集（与 docs 表一致） */
export function getCapabilityRoleHint(roleName) {
  if (!roleName) return ''
  const map = {
    [FINE_MEMBER_REGISTER_ADMIN]: '（对应 ① 会员注册）',
    [FINE_GIFT_INTEGRATION_ADMIN]: '（对应 ② 赠送积分）',
    [FINE_GIFT_VERIFY_CARD_ADMIN]: '（对应 ③ 赠送卡券）',
    [FINE_VERIFY_CARD_ADMIN]: '（对应 ④ 卡券核销）',
    [FINE_INTEGRATION_ACTIVATION_ADMIN]: '（对应 ⑤ 积分激活）',
    [FINE_SUPPLIER_ADMIN]: '（对应 ⑧ 商品供应商管理）',
    [INTEGRATION_MANAGER_ROLE_NAME]: '（兼容 ②③⑤ 积分侧）',
    [SYSTEM_ADMIN_ROLE_NAME]: '（兼容 ②③⑤ 积分侧）',
    [MEMBER_ADMIN_ROLE_NAME]: '（兼容 ① + GET 赠送原因分类）',
    [VERIFICATION_ADMIN_ROLE_NAME]: '（兼容 ④ 核销侧）',
    [SUPER_ADMIN_ROLE_NAME]: '（全量）'
  }
  return map[roleName] || ''
}



// WEBPACK FOOTER //
// ./src/utils/integrationModuleScopeConfig.js